/**
 * Tiny per-route SEO helper.
 *
 * Updates document.title + meta[name=description] + canonical + the most
 * common og:/twitter: tags on mount and whenever its props change. No
 * `react-helmet` dependency — Google reads these even when set imperatively.
 *
 * Pass `noindex={true}` for private / app pages we don't want crawled.
 *
 * NOTE: This is purely a client-side update — for true crawler-safe SEO on a
 * SPA you'd need SSR or pre-rendering. Most modern crawlers (Googlebot,
 * Bingbot, Twitterbot, LinkedIn) DO execute JS and pick up these updates, so
 * for our scale this is more than enough.
 */
import { useEffect } from "react";

const SITE = "https://admissioncrafts.com";
const DEFAULT_OG_IMAGE = `${SITE}/logo.png`;

function setMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    // selector e.g. "meta[name='description']" — extract name/property
    const m = selector.match(/(\w+)=['"]([^'"]+)['"]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
}

function setCanonical(url) {
  let el = document.head.querySelector("link[rel='canonical']");
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
}

export default function Seo({
  title,
  description,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  noindex = false,
}) {
  useEffect(() => {
    const fullTitle = title ? `${title} · AdmissionCrafts` : "AdmissionCrafts · Find Your True North Star";
    const desc = description || "MHT-CET college predictor + complete CAP guide for Maharashtra engineering aspirants.";
    const url = `${SITE}${path.startsWith("/") ? path : "/" + path}`;

    document.title = fullTitle;

    setMeta("meta[name='description']", { content: desc });
    setMeta("meta[name='robots']", { content: noindex ? "noindex,nofollow" : "index,follow,max-image-preview:large" });

    setMeta("meta[property='og:title']", { property: "og:title", content: fullTitle });
    setMeta("meta[property='og:description']", { property: "og:description", content: desc });
    setMeta("meta[property='og:url']", { property: "og:url", content: url });
    setMeta("meta[property='og:image']", { property: "og:image", content: image });
    setMeta("meta[property='og:type']", { property: "og:type", content: "website" });
    setMeta("meta[property='og:site_name']", { property: "og:site_name", content: "AdmissionCrafts" });

    setMeta("meta[name='twitter:card']", { content: "summary_large_image" });
    setMeta("meta[name='twitter:title']", { content: fullTitle });
    setMeta("meta[name='twitter:description']", { content: desc });
    setMeta("meta[name='twitter:image']", { content: image });

    setCanonical(url);
  }, [title, description, path, image, noindex]);

  return null;
}
